'use client';

import { useId } from 'react';

import type { APlusItem, APlusItems } from '../../types';

import type { AplusLayoutDefaultProps } from '../types';
import { Item } from '../../atoms/Item/Item.client';
import { useServiceLayer } from '../../hooks/useServiceLayer/useServiceLayer';
import { useAplusData } from '../../hooks/useAplusData/useAplusData';

export type ListItemsProps = {
  items: APlusItems;
  /**
   * Renders an action next to a service price, e.g. the call-center pencil that
   * overrides it. Return `null` for services that may not be acted on.
   */
  renderPriceAction?: (item: APlusItem) => React.ReactNode;
  /**
   * Renders a line below a service row, under its price, e.g. the price a call-center
   * agent entered. Return `null` for services that need none.
   */
  renderPriceDetails?: (item: APlusItem) => React.ReactNode;
} & AplusLayoutDefaultProps;

/**
 * Renders APlus services as a flat list of selectable items.
 */
export const ListItems = ({
  items,
  currency,
  quantity,
  onChangeHandler,
  triggerOnChangeHandlerOnInit,
  controlled,
  renderPriceAction,
  renderPriceDetails,
}: ListItemsProps) => {
  const componentId = useId();

  // the layout keeps its own selection where nothing is saved yet, the product page being the one
  // that does; the flows that write every change to the basket hand theirs in
  const own = useAplusData(items, onChangeHandler, triggerOnChangeHandlerOnInit);
  const aplusData = controlled?.data ?? own.aplusData;
  const handleItemChange = controlled?.onItemChange ?? own.handleItemChange;
  const handleCountChange = controlled?.onCountChange ?? own.handleCountChange;
  const { setDialogContent, serviceLayer } = useServiceLayer();

  return (
    <>
      <div className="grid grid-cols-[auto_auto_1fr_auto_auto] gap-y-1.5">
        {[...aplusData.entries()].map(([uuid, itemData]) => {
          const id = `${componentId}-${uuid}`;
          const { mandatory, serviceIcon, name, price, free, serviceLayerId, serviceCount } =
            itemData.item;

          return (
            <div
              className="border-color-text-darktransparent bg-color-grey-light text-size-body-sm col-span-5 grid grid-cols-subgrid rounded border p-1 pl-0.5"
              key={id}
            >
              <Item
                uuid={uuid}
                mandatory={mandatory}
                serviceIcon={serviceIcon}
                name={name}
                price={price}
                free={free}
                currency={currency}
                quantity={quantity}
                id={id}
                selected={aplusData.get(uuid)?.selected || false}
                value={aplusData.get(uuid)?.value}
                serviceCount={serviceCount}
                onInfoClickHandler={() =>
                  setDialogContent({
                    header: name || '',
                    serviceLayerId,
                  })
                }
                onItemChangeHandler={handleItemChange}
                onCountChangeHandler={handleCountChange}
                priceActionSlot={renderPriceAction?.(itemData.item)}
                priceDetailsSlot={renderPriceDetails?.(itemData.item)}
              />
            </div>
          );
        })}
      </div>
      {serviceLayer}
    </>
  );
};
